import { useEffect, useState, useRef } from "react";
import { motion, useInView, useSpring, useMotionValue } from "framer-motion";
import {
  Car02Icon,
  UserGroupIcon,
  StarIcon,
  Certificate01Icon,
} from "hugeicons-react";
import { useMobilePerformance } from "../../hooks/useMobilePerformance";

const stats = [
  {
    icon: <Car02Icon size={24} />,
    value: 1200,
    suffix: "+",
    label: "سيارة تم خدمتها",
  },
  {
    icon: <UserGroupIcon size={24} />,
    value: 950,
    suffix: "+",
    label: "عميل سعيد",
  },
  {
    icon: <StarIcon size={24} />,
    value: 100,
    suffix: "%",
    label: "رضا العملاء",
  },
  {
    icon: <Certificate01Icon size={24} />,
    value: 8,
    suffix: "+",
    label: "سنوات خبرة",
  },
];

function Counter({ value, suffix, isMobile }: { value: number; suffix: string; isMobile: boolean }) {
  const ref = useRef(null);
  const motionValue = useMotionValue(0);
  const springValue = useSpring(motionValue, {
    damping: isMobile ? 40 : 30,  // Faster settling on mobile
    stiffness: isMobile ? 150 : 100,
  });
  const isInView = useInView(ref, { once: true, margin: isMobile ? "0px" : "-100px" });

  useEffect(() => {
    if (isInView) {
      motionValue.set(value);
    }
  }, [isInView, value, motionValue]);

  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    return springValue.on("change", (latest) => {
      setDisplayValue(Math.floor(latest));
    });
  }, [springValue]);

  return (
    <span ref={ref} className="font-mono tracking-tighter">
      {displayValue}
      <span className="text-primary ml-1">{suffix}</span>
    </span>
  );
}

export default function Stats() {
  const { isMobile, shouldDisableHeavyEffects } = useMobilePerformance();
  
  return (
    <section className="py-16 md:py-24 lg:py-32 bg-[#020000] relative overflow-hidden">
      {/* HUD Grid Background - Hidden on mobile for performance */}
      {!isMobile && (
        <div className="absolute inset-0 bg-[url('/grid.svg')] bg-[length:50px_50px] opacity-[0.03] mix-blend-overlay" />
      )}
      
       {/* Seamless Masks */}
       <div className="absolute top-0 left-0 right-0 h-16 md:h-32 bg-gradient-to-b from-[#020000] to-transparent z-10 pointer-events-none" />
       <div className="absolute bottom-0 left-0 right-0 h-16 md:h-32 bg-gradient-to-t from-[#020000] to-transparent z-10 pointer-events-none" />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: isMobile ? "0px" : "-50px" }}
              transition={{ 
                delay: isMobile ? index * 0.05 : index * 0.1,
                duration: isMobile ? 0.35 : 0.5,
                ease: [0.32, 0.72, 0, 1]
              }}
              className={`relative group p-4 md:p-8 rounded-2xl border border-white/5 bg-[#050505]/40 overflow-hidden
                ${isMobile ? '' : 'backdrop-blur-sm'}`}
            >
              {/* HUD Borders - Simplified on mobile */}
              <div className="absolute top-0 left-0 w-3 md:w-4 h-3 md:h-4 border-t-2 border-l-2 border-primary/40 rounded-tl-lg" />
              <div className="absolute bottom-0 right-0 w-3 md:w-4 h-3 md:h-4 border-b-2 border-r-2 border-primary/40 rounded-br-lg" />
              
              {/* Scanline Animation - DISABLED on mobile (very heavy) */}
              {!shouldDisableHeavyEffects && (
                <motion.div 
                  animate={{ top: ["0%", "100%", "0%"] }}
                  transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
                  className="absolute left-0 right-0 h-[1px] bg-primary/30 z-0 pointer-events-none"
                  style={{ willChange: 'top' }}
                />
              )}

              <div className="relative z-10 flex flex-col items-center">
                <div className={`w-10 md:w-14 h-10 md:h-14 mb-3 md:mb-6 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-primary transition-transform duration-500
                  ${isMobile ? '' : 'group-hover:scale-110 shadow-[0_0_20px_rgba(220,20,60,0.2)]'}`}>
                  {stat.icon}
                </div>
                
                <div className="text-3xl md:text-5xl lg:text-6xl font-black text-white mb-1 md:mb-3">
                  <Counter value={stat.value} suffix={stat.suffix} isMobile={isMobile} />
                </div>
                
                <div className="text-gray-500 font-black text-[10px] md:text-xs uppercase tracking-[0.2em] md:tracking-[0.4em] text-center">
                  {stat.label}
                </div>
              </div>

              {/* Tactical Overlay - Desktop only */}
              {!isMobile && (
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
